import React, { useState } from 'react';
import { 
  CheckCircle2, 
  AlertCircle, 
  ChevronDown, 
  ChevronUp,
  Lightbulb,
  FileText,
  Code,
  HelpCircle,
  History,
  AlertTriangle,
  Settings,
  Link2
} from 'lucide-react';

interface CompletenessBarProps {
  score: number;
  missingFields: string[];
  suggestions: string[];
  onFieldClick?: (field: string) => void;
}

// Map field names to icons
const FIELD_ICONS: Record<string, React.ReactNode> = {
  'Descrição': <FileText className="w-4 h-4" />,
  'Documentação': <FileText className="w-4 h-4" />,
  'URL da Documentação': <Link2 className="w-4 h-4" />,
  'Guia de Configuração': <Settings className="w-4 h-4" />,
  'Exemplos de Código': <Code className="w-4 h-4" />,
  'Casos de Uso': <Lightbulb className="w-4 h-4" />,
  'Changelog': <History className="w-4 h-4" />,
  'FAQ': <HelpCircle className="w-4 h-4" />,
  'Limitações Conhecidas': <AlertTriangle className="w-4 h-4" />,
  'Requisitos de Ambiente': <Settings className="w-4 h-4" />,
  'Templates Complementares': <Link2 className="w-4 h-4" />,
};

export function CompletenessBar({
  score,
  missingFields,
  suggestions,
  onFieldClick,
}: CompletenessBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine color based on score
  const getColorClass = () => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTextColorClass = () => {
    if (score >= 80) return 'text-green-700';
    if (score >= 50) return 'text-yellow-700';
    return 'text-red-700';
  };

  const getBgColorClass = () => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 50) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusText = () => {
    if (score >= 80) return 'Documentação completa';
    if (score >= 50) return 'Documentação parcial';
    return 'Documentação incompleta';
  };

  const getStatusIcon = () => {
    if (score >= 80) return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    return <AlertCircle className="w-5 h-5 text-yellow-600" />;
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${getBgColorClass()}`}>
      {/* Header - Always visible */}
      <div
        className="p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  Completude da Documentação
                </span>
                <span className={`text-sm font-bold ${getTextColorClass()}`}>
                  {score}%
                </span>
              </div>
              <p className="text-sm text-gray-600">{getStatusText()}</p>
            </div>
          </div>

          <button
            type="button"
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getColorClass()}`}
              style={{ width: `${score}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 bg-white">
          {/* Missing Fields */}
          {missingFields.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Campos faltantes ({missingFields.length})
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {missingFields.map((field, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onFieldClick?.(field)}
                    className="flex items-center gap-2 p-2 text-left text-sm text-gray-600 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                  >
                    {FIELD_ICONS[field] || <FileText className="w-4 h-4" />}
                    <span>{field}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-500" />
                Sugestões de melhoria
              </h4>
              <ul className="space-y-2">
                {suggestions.slice(0, 5).map((suggestion, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-600"
                  >
                    <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-1.5 flex-shrink-0" />
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
              {suggestions.length > 5 && (
                <p className="text-xs text-gray-500 mt-2">
                  +{suggestions.length - 5} mais sugestões
                </p>
              )}
            </div>
          )}

          {/* All Complete */}
          {missingFields.length === 0 && (
            <div className="mt-4 flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">
                Parabéns! Toda a documentação está preenchida.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CompletenessBar;
